/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, June 19, 2014
 * For information or permission request, email info@aetonixsystems.com
 */


var Model = require("../types/DebouncedModel.js");
var extender = Model.makeExtender;
var debounce = require("debounce");
var xtend = require("xtend");
var pipe = require("fj-pipe");
var prop = require("prop");
var par = require("par");
var getList = prop("list");
var Promise = require("promise");
// eslint-disable-next-line node/no-missing-require
const AsyncStorage = require("@react-native-async-storage/async-storage").default;

var updateOutgoing = par(createListUpdater, recalculateOutgoing);
var updateIncoming = par(createListUpdater, recalculateIncoming);

var TRACKED = [
	"incoming",
	"outgoing",
	"contacts",
	"managed",
	"groups"
];

// Wait 100 MS between recalculating on availability changes
var AVAILABILITY_DEBOUNCE = 100;

module.exports = Model({
	name: "homepage",
	init: init,
	uninit: uninit,
	triggers: triggers,
	listeners: listeners,

	_available: null,
	_contacts: null,

	recalculateContacts: recalculateContacts,
	recalculateIncoming: recalculateIncoming,
	recalculateOutgoing: recalculateOutgoing,
	recalculateManagees: recalculateManagees,
	recalculateGroups: recalculateGroups,
	loadCached: loadCached,

	debounce: 100,
});

function init() {
	this._invitesIn = [];
	this._managedIn = [];
	this._orgGroupInvitesIn = [];
	this._invitesOut = [];
	this._managedOut = [];
	this._invitesEmail = [];

	this._available = {};
	this._contacts = [];
	this._managees = [];
	this._groups = [];


	var data = TRACKED.reduce(function (result, name) {
		result[name] = [];
		result.displaying[name] = false;
		result[name + "_count"] = 0;
		return result;
	}, {
		displaying: {},
		hideOffline: {
			contacts: false,
			managed: false,
		},
		isSearching: false,
	});

	this.push(data);

	this.loadCached();
}

function uninit() {
	this._invitesIn = null;
	this._managedIn = null;
	this._orgGroupInvitesIn = null;
	this._invitesOut = null;
	this._managedOut = null;
	this._invitesEmail = null;
	this._available = null;
	this._contacts = null;
	this._groups = null;
}

function triggers() {
	var display_toggles = TRACKED.reduce(function (result, name) {
		result["toggle:" + name] = make_toggle(name);
		return result;
	}, {
		"toggle:offline:contacts": toggle_contacts_offline,
		"toggle:offline:managed": toggle_managed_offline
	});

	return display_toggles;
}

function listeners() {
	return {
		"availability:updated": debounce(handle_availability_updated, AVAILABILITY_DEBOUNCE),
		"contacts:updated": handle_contacts_updated,
		"groups:updated": handle_groups_updated,

		"incomingrequests:updated": updateIncoming("_invitesIn"),
		"incomingmanagementrequests:updated": updateIncoming("_managedIn"),
		"incomingorggrouprequests:updated": updateIncoming("_orgGroupInvitesIn"),


		"accountinvites:updated": pipe(getList, updateOutgoing("_invitesEmail").bind(this)),
		"outgoingrequests:updated": updateOutgoing("_invitesOut"),
		"outgoingmanagementrequests:updated": updateOutgoing("_managedOut"),

		"managedusers:updated": handle_managedusers_updated,
		"manageduserscount:updated": handle_managedusers_count,
		"nologinaccounts:updated": extender("nologin"),
		"trialaccounts:updated": extender("trial"),
	};
}

function loadCached() {
	var model = this;
	Promise.all(TRACKED.map(getShouldDisplay)).then(function(results) {
		var saved = results.reduce(function(displaying, shouldDisplay, index){
			var name = TRACKED[index];
			displaying[name] = shouldDisplay;
			return displaying;
		}, {});

		var offline_contacts = saved.offline_contacts;
		var offline_managed = saved.offline_managed;

		model.extend({
			displaying: saved,
			hideOffline: {
				contacts: offline_contacts,
				managed: offline_managed
			}
		});
	});
}

/*
	Create handlers for the varous incoming requests
	The handlers should save the list locally to a variable that looks like this._LISTNAMEHERE
	Create a recalculate_incoming function which will take both incoming lists, and concatenate them
	Before it does this, it will map over the individual lists, and add some sort of flag. that can be done with list.map(function(item){ return xtend(item, {flagNameHere:true})});
	It will then take the merged lists, and call this.extend({incoming: newlisthere})

	Sommething similar will need to be done for outgoing requests
*/
function recalculateIncoming() {
	var mappedInviteList = this._invitesIn.map(function (item) {
		return xtend(item, {
			contactInvite: true
		});
	});
	var mappedManagedInList = this._managedIn.map(function (item) {
		return xtend(item, {
			managedInvite: true
		});
	});

	var mappedOrgGroupInviteInList = this._orgGroupInvitesIn.map(function (item) {
		return xtend(item, {
			orgGroupInvite: true
		});
	});

	var mergedLists = mappedInviteList.concat(mappedManagedInList).concat(mappedOrgGroupInviteInList);

	this.extend({
		incoming_count: mergedLists.length,
		incoming: mergedLists
	});
}

function createListUpdater(recalculate, name) {
	return function (data) {
		this[name] = data;
		recalculate.call(this);
	};
}

function recalculateOutgoing() {
	var mappedAccountsOutList = this._invitesEmail.map(function(item){
		return xtend(item, {
			accountInvite: true
		});
	});
	var mappedInviteOutList = this._invitesOut.map(function (item) {
		return xtend(item, {
			contactRequest: true
		});
	});
	var mappedManagedOut = this._managedOut.map(function (item) {
		return xtend(item, {
			managedRequests: true
		});
	});

	var mergedOutgoingList = mappedInviteOutList.concat(mappedManagedOut).concat(mappedAccountsOutList);
	this.extend({
		outgoing_count: mergedOutgoingList.length,
		outgoing: mergedOutgoingList
	});
}

function recalculateContacts() {
	var allcontacts = this._contacts;
	if(!allcontacts) return;
	var managees = this._managees.map(function (person){
		return person._id;
	});

	// Filter out 'managed' contacts.
	var nonManagedContacts = allcontacts.filter(par(isnt_managed, managees));

	var contacts = nonManagedContacts;

	var hidingOffline = this.data.hideOffline.contacts;
	var available = this._available;
	if(hidingOffline)
		contacts = contacts.filter(function(contact){
			var id = contact._id;
			return available[id];
		});

	var sorted = contacts.sort(compare_names);

	this.extend({
		contacts_count: nonManagedContacts.length,
		contacts: sorted
	});
}

function isnt_managed(managees, contact) {
	return !managees.some(function (managee){
		return contact._id === managee;
	});
}

function getShouldDisplay(name) {
	return AsyncStorage.getItem("home_display_" + name).then(function(existing){
		if (existing === null) return true;
		return !!existing;
	});
}

function setShouldDisplay(name, displaying) {
	var key = "home_display_" + name;
	if (!displaying) AsyncStorage.setItem(key, "");
	else AsyncStorage.setItem(key, Date.now().toString());
}

function toggle_contacts_offline() {
	var current = this.data.hideOffline;
	var newDisplay = !current.contacts;
	var update = xtend(current, {
		contacts: newDisplay
	});

	setShouldDisplay("offline_contacts", newDisplay);

	this.extend({
		hideOffline: update
	});
	this.recalculateContacts();

	this.emit("usermetrics:trigger:track", `toggle offline contacts to ${newDisplay ? "on" : "off"} on homepage`);
}


function toggle_managed_offline() {
	var current = this.data.hideOffline;
	var newDisplay = !current.managed;
	var update = xtend(current, {
		managed: newDisplay
	});

	setShouldDisplay("offline_managed", newDisplay);

	this.extend({
		hideOffline: update
	});
	this.recalculateManagees();

	this.emit("usermetrics:trigger:track", newDisplay ? "hide offline managed users on homepage" : "show offline managed users on homepage");
}

function make_toggle(name) {
	return function () {
		var shouldDisplay = !this.data.displaying[name];

		setShouldDisplay(name, shouldDisplay);

		var displayUpdate = {};
		displayUpdate[name] = shouldDisplay;
		var newDisplay = xtend(this.data.displaying, displayUpdate);

		this.extend({
			displaying: newDisplay
		});

		const sectionToName = {
			"managed": "managed users",
			"contacts": "contacts",
			"groups": "groups",
		};
		this.emit("usermetrics:trigger:track", `${shouldDisplay ? "unfold" : "fold"} ${sectionToName[name]} section on homepage`, {
			displaying: shouldDisplay
		});
	};
}

function recalculateGroups(){
	var groups = this._groups;

	var sorted = groups.sort(compare_group_names);

	this.extend({
		groups_count: groups.length,
		groups: sorted
	});
}

function handle_groups_updated(groups){
	this._groups = groups;
	this.recalculateGroups();
}

function handle_contacts_updated(contacts) {
	this._contacts = contacts;
	this.recalculateContacts();
}

function handle_managedusers_updated(managees) {
	this._managees = managees;

	this.recalculateManagees();
	this.recalculateContacts();
}

function handle_managedusers_count(count) {
	this.extend({
		managed_users_count: count
	});
}

function recalculateManagees() {
	var original_managees = this._managees;
	var managees = original_managees;

	var hidingOffline = this.data.hideOffline.managed;
	var available = this._available;
	if(hidingOffline)
		managees = managees.filter(function(contact){
			var id = contact._id;
			return available[id];
		});

	var sorted = managees.sort(compare_names);

	this.extend({
		managed_count: original_managees.length,
		managed: sorted
	});
}

function handle_availability_updated(available) {
	this._available = available;
	this.recalculateContacts();
}

function compare_group_names(group1, group2){
	var name1 = (group1.name || "");
	var name2 = (group2.name || "");

	name1 = name1.toLowerCase();
	name2 = name2.toLowerCase();

	if(name1 < name2) return -1;
	if(name2 < name1) return 1;
	return 0;
}

function compare_names(person1, person2) {
	var name1 = (person1.fname || "") + " " + (person1.lname || "");
	var name2 = (person2.fname || "") + " " + (person2.lname || "");

	name1 = name1.toLowerCase();
	name2 = name2.toLowerCase();

	if (name1 < name2) return -1;
	if (name2 < name1) return 1;
	return 0;
}